import { useQueryClient } from 'react-query';
import { useQueryGetProject } from '../../../../core/queries/projects.queries';
import { QueryKeys } from '../../../../core/queries/query-keys.constants';
import { ConfirmationModal } from '../../../../ui/components/pure/modal-content/confirmation-modal.component';
import { useMutationDeleteProject } from '../../settings.queries';

export interface DeleteProjectModalProps {
  projectId: string;
  hasChildProjects?: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

export function DeleteProjectModal(props: DeleteProjectModalProps) {
  const queryClient = useQueryClient();
  const projectResult = useQueryGetProject(props.projectId);
  const deleteMutation = useMutationDeleteProject();

  const onConfirm = async () => {
    if (projectResult.data?.id) {
      await deleteMutation.mutateAsync({ projectID: projectResult.data.id });
      queryClient.invalidateQueries(QueryKeys.SETTINGS);
      queryClient.invalidateQueries([QueryKeys.PROJECTS, projectResult.data.id]);
      queryClient.invalidateQueries([QueryKeys.PROJECTS]);
      props.onSuccess();
    }
  };

  const projectName = projectResult.data?.name;

  const errorMessage = !projectName
    ? 'Sorry, there was an error loading the Project. Please close the modal and try again'
    : undefined;

  if (props.hasChildProjects) {
    return (
      <ConfirmationModal
        title="Delete Project?"
        onCancel={props.onCancel}
        onConfirm={onConfirm}
        warning={`This Project cannot be deleted because it has child projects.
           If you really want to delete it, you need to delete all child projects first.`}
        isLoading={projectResult.isLoading}
        content={[projectResult.data?.name || '']}
        noteRequired={false}
        cancelButton={{
          label: 'Cancel',
          color: 'secondary'
        }}
      />
    );
  }

  return (
    <ConfirmationModal
      title="Delete Project?"
      onCancel={props.onCancel}
      onConfirm={onConfirm}
      warning={`Are you sure? You only want to do this if it will not be used ever again, or never was in the first place. \n You can just disable the hub if you want to temporarily prevent deliveries.`}
      isLoading={projectResult.isLoading || deleteMutation.isLoading}
      content={[projectResult.data?.name || '']}
      errorMessage={errorMessage}
      noteRequired={false}
      actionButton={{
        label: 'Delete',
        color: 'danger'
      }}
      cancelButton={{
        label: 'Cancel',
        color: 'secondary'
      }}
    />
  );
}
